// covered-by: frontends/carrie/src/routes/ask-puntt/utils/__tests__/parseCommentSections.test.ts
export type CommentSection = {
  label: string;
  body: string;
};

const CODE_FENCE_OPEN = "\uE000";
const CODE_FENCE_CLOSE = "\uE001";
const CODE_FENCE_PLACEHOLDER = /\uE000(\d+)\uE001/g;

const LABELED_BLOCK_RE = /^\*\*([^*]+):\*\*\s*([\s\S]*)$/;

/**
 * Mirror of Markdown.tsx normalizeNewlines — keeps section boundaries aligned
 * with how the comment body renders in fallback (single-column) mode.
 */
export function normalizeCommentNewlines(s: string): string {
  const codes: string[] = [];
  const protectedInput = s.replace(/```[\s\S]*?```/g, (m) => {
    codes.push(m);
    return `${CODE_FENCE_OPEN}${codes.length - 1}${CODE_FENCE_CLOSE}`;
  });

  let out = protectedInput.replace(/\n{3,}/g, "\n\n");
  out = out.replace(/\n\n([.,;:!?)\]])/g, "$1");
  out = out.replace(/\n\n(["'])(\s*[.,;:!?a-z])/g, "$1$2");
  out = out.replace(/\n\n([—–])\s/g, " $1 ");

  return out.replace(CODE_FENCE_PLACEHOLDER, (_, i) => codes[Number(i)]);
}

/** Kebab-case slug for section testids (e.g. Issue -> issue, Problema -> problema). */
export function slugifyCommentSectionLabel(label: string): string {
  return label
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export function isRiskCommentSection(section: CommentSection): boolean {
  return slugifyCommentSectionLabel(section.label) === "risk";
}

/** Pull Risk out of parsed sections so it can render in the comment header row. */
export function extractRiskSection(sections: CommentSection[]): {
  risk: CommentSection | null;
  sections: CommentSection[];
} {
  const riskIndex = sections.findIndex(isRiskCommentSection);
  if (riskIndex === -1) {
    return { risk: null, sections };
  }
  return {
    risk: sections[riskIndex],
    sections: sections.filter((_, index) => index !== riskIndex),
  };
}

/**
 * Split AI review comments into labeled sections (`**Issue:**`, `**Fix:**`, etc.).
 * Returns an empty array for unstructured / human comments so the UI can fall
 * back to single-column markdown.
 */
export function parseCommentSections(description: string): CommentSection[] {
  const normalized = normalizeCommentNewlines(description.trim());
  if (!normalized) return [];

  const blocks = normalized.split(/\n\n+/);
  const sections: CommentSection[] = [];

  for (const block of blocks) {
    const trimmed = block.trim();
    if (!trimmed) continue;

    const match = trimmed.match(LABELED_BLOCK_RE);
    if (match) {
      sections.push({
        label: match[1].trim(),
        body: match[2].trim(),
      });
    } else if (sections.length > 0) {
      const last = sections[sections.length - 1];
      last.body = last.body ? `${last.body}\n\n${trimmed}` : trimmed;
    } else {
      return [];
    }
  }

  return sections;
}

const MARKDOWN_HEADING_LINE_RE = /^#{1,6}\s/m;

const INLINE_CODE_FENCE_OPEN = "\uE002";
const INLINE_CODE_FENCE_CLOSE = "\uE003";
const INLINE_CODE_FENCE_PLACEHOLDER = /\uE002(\d+)\uE003/g;

/**
 * AI review comments quote product names and verbatim values in inline
 * backticks (`Honey Almond`). In the review UI those should read as bold body
 * text, not monospace code styling.
 */
export function rewriteInlineCodeToBold(markdown: string): string {
  const fences: string[] = [];
  const protectedInput = markdown.replace(/```[\s\S]*?```/g, (block) => {
    fences.push(block);
    return `${INLINE_CODE_FENCE_OPEN}${fences.length - 1}${INLINE_CODE_FENCE_CLOSE}`;
  });

  const rewritten = protectedInput.replace(
    /`([^`\n]+)`/g,
    (_match, inner: string) => `**${inner}**`,
  );

  return rewritten.replace(
    INLINE_CODE_FENCE_PLACEHOLDER,
    (_, index) => fences[Number(index)],
  );
}

/** Strip a leading markdown heading marker so product names can render inline. */
function stripMarkdownHeadingLine(line: string): string {
  const trimmed = line.trim();
  const match = trimmed.match(/^#{1,6}\s+(.+)$/);
  return match ? match[1].trim() : trimmed;
}

/**
 * Markdown for one labeled comment section inside CommentSectionColumn.
 * Single-line bodies stay inline with the label (`**Issue:** Logo color`).
 * Multi-paragraph bodies keep the product-name lead inline as `**bold**`
 * spans in the same paragraph as the label, not a separate block.
 */
export function commentSectionMarkdown(section: CommentSection): string {
  const body = section.body.trim();
  const label = `**${section.label}:**`;
  if (!body) return label;

  const paragraphs = body.split(/\n\n+/);
  let markdown: string;
  if (paragraphs.length === 1) {
    const line = paragraphs[0].trim();
    if (MARKDOWN_HEADING_LINE_RE.test(line)) {
      markdown = `${label} **${stripMarkdownHeadingLine(line)}**`;
    } else {
      markdown = `${label} ${body}`;
    }
  } else {
    const [lead, ...tail] = paragraphs;
    const leadTrimmed = stripMarkdownHeadingLine(lead);
    const leadMarkdown =
      leadTrimmed.startsWith("**") ||
      leadTrimmed.startsWith("*") ||
      leadTrimmed.startsWith("_")
        ? leadTrimmed
        : `**${leadTrimmed}**`;

    markdown = `${label} ${leadMarkdown}\n\n${tail.join("\n\n")}`;
  }

  return rewriteInlineCodeToBold(markdown);
}

/**
 * Rebuild labeled AI comment markdown so product names and legacy `###`
 * headings render as inline bold spans beside section labels. Returns null
 * when the description is unstructured (human freeform prose).
 */
export function formatStructuredCommentMarkdown(
  description: string,
): string | null {
  const sections = parseCommentSections(description);
  if (sections.length === 0) return null;
  return sections.map(commentSectionMarkdown).join("\n\n");
}
